import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Loader2, Save } from 'lucide-react'
import { useAuth } from '@/features/auth'
import { useUpdateProfile } from '../hooks/use-profile'
import { useToast } from '@/hooks/use-toast'

const profileSchema = z.object({
  name: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres'),
  email: z.string().email('Email invalido'),
})

type ProfileFormValues = z.infer<typeof profileSchema>

export function ProfileTab() {
  const { userProfile, user, currentOrganization } = useAuth()
  const { toast } = useToast()
  const updateProfile = useUpdateProfile()

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      name: userProfile?.full_name ?? '',
      email: user?.email ?? '',
    },
  })

  const onSubmit = async (data: ProfileFormValues) => {
    try {
      await updateProfile.mutateAsync(data)
      toast({
        title: 'Perfil atualizado',
        description: 'Suas informacoes foram atualizadas com sucesso.',
      })
    } catch {
      toast({
        title: 'Erro',
        description: 'Nao foi possivel atualizar o perfil.',
        variant: 'destructive',
      })
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Informacoes do Perfil</CardTitle>
        <CardDescription>
          Atualize suas informacoes pessoais
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome</FormLabel>
                  <FormControl>
                    <Input placeholder="Seu nome" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="seu@email.com"
                      {...field}
                      disabled
                    />
                  </FormControl>
                  <FormMessage />
                  <p className="text-xs text-muted-foreground">
                    O email nao pode ser alterado
                  </p>
                </FormItem>
              )}
            />

            {currentOrganization && (
              <div className="pt-4 border-t">
                <p className="text-sm text-muted-foreground">Organizacao</p>
                <p className="font-medium">{currentOrganization.name}</p>
              </div>
            )}

            <div className="flex justify-end pt-4">
              <Button type="submit" disabled={updateProfile.isPending}>
                {updateProfile.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Salvar alteracoes
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  )
}
